import {
  HandshakeIcon,
  ZapIcon,
  ReceiptTextIcon,
  BadgeCheckIcon,
  Trash2Icon,
  SofaIcon,
  RefrigeratorIcon,
  LeafIcon,
  HardHatIcon,
  BoxesIcon,
  TreePineIcon,
  Building2Icon,
  MapPinIcon,
  CalendarClockIcon,
  ShieldCheckIcon,
  LayersIcon,
  TruckIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const PHONE_DISPLAY = '(760) 783-8361'
export const PHONE_HREF = 'tel:+17607838361'

export const LOGO_URL =
  'https://cdn.magicpatterns.com/uploads/3PH5ehieNrV2JDtQkPTKyz/Photoroom_20260916_082804.png'
export const TRAILER_IMAGE_URL =
  'https://cdn.magicpatterns.com/uploads/7dS3LwFFYRSCmNcraeBLnV/javiers1.png'
export const HERO_BG_URL =
  'https://cdn.magicpatterns.com/patterns/generated-images/1bf16550-381b-4831-a524-9091f99981ed.jpg'

export interface TrustBadge {
  icon: LucideIcon
  title: string
  body: string
}

export const trustBadges: TrustBadge[] = [
  {
    icon: CalendarClockIcon,
    title: 'Same-Day Available',
    body: 'Call today, we can often haul today or tomorrow.',
  },
  {
    icon: ReceiptTextIcon,
    title: 'Transparent Pricing',
    body: 'Your price over the phone before we touch anything.',
  },
  {
    icon: MapPinIcon,
    title: 'Local Expert',
    body: 'North County owned and operated — you talk to Javier.',
  },
]

export interface Differentiator {
  icon: LucideIcon
  title: string
  body: string
}

export const differentiators: Differentiator[] = [
  {
    icon: HandshakeIcon,
    title: 'Real Local Service',
    body: 'You talk to Javier. You get honest pricing. You get someone who treats your property like his own.',
  },
  {
    icon: ZapIcon,
    title: 'Fast & Flexible',
    body: 'Same-day or next-day removal. We work around your schedule. No corporate delays.',
  },
  {
    icon: ReceiptTextIcon,
    title: 'Transparent Pricing',
    body: "No hidden charges. No surprises. You know what you'll pay before we touch anything.",
  },
  {
    icon: BadgeCheckIcon,
    title: 'Professional & Reliable',
    body: "We've served North County families for years. We show up on time. We do the job right.",
  },
]

export interface Service {
  icon: LucideIcon
  label: string
  body: string
  items: string[]
}

export const services: Service[] = [
  {
    icon: Trash2Icon,
    label: 'Junk & Debris Removal',
    body: 'General household junk hauled out of the garage, attic, shed or side yard.',
    items: ['Boxes & clutter', 'Mattresses', 'Scrap metal', 'Old toys & bikes'],
  },
  {
    icon: SofaIcon,
    label: 'Furniture Removal',
    body: 'Heavy, awkward pieces carried out without scuffing your floors or walls.',
    items: ['Couches & recliners', 'Beds & dressers', 'Tables & desks', 'Office chairs'],
  },
  {
    icon: RefrigeratorIcon,
    label: 'Appliance Removal',
    body: 'Disconnected, lifted and hauled — recycled wherever we can.',
    items: ['Fridges & freezers', 'Washers & dryers', 'Stoves & ovens', 'Water heaters'],
  },
  {
    icon: LeafIcon,
    label: 'Yard Waste Removal',
    body: 'Green waste and storm debris cleared from the yard and hauled off.',
    items: ['Branches & palm fronds', 'Grass & leaves', 'Old fencing', 'Dirt & pots'],
  },
  {
    icon: HardHatIcon,
    label: 'Construction Debris',
    body: 'Post-remodel cleanup so your contractor — or you — can finish the job.',
    items: ['Drywall & lumber', 'Tile & concrete', 'Carpet & padding', 'Roofing scrap'],
  },
  {
    icon: BoxesIcon,
    label: 'Complete Cleanouts',
    body: 'Whole-property clears for moves, estates, evictions and rentals.',
    items: ['Garage cleanouts', 'Estate cleanouts', 'Rental turnovers', 'Storage units'],
  },
]

export interface ExtraService {
  icon: LucideIcon
  label: string
}

export const extraServices: ExtraService[] = [
  { icon: TreePineIcon, label: 'Tree Trimming & Removal' },
  { icon: Building2Icon, label: 'Residential & Commercial' },
  { icon: TruckIcon, label: 'Truck & Dump Trailer Hauling' },
]

export interface ServiceArea {
  name: string
  lat: number
  lng: number
  note: string
}

export const serviceAreaLocations: ServiceArea[] = [
  { name: 'Oceanside', lat: 33.1959, lng: -117.3795, note: 'Garage and rental cleanouts weekly.' },
  { name: 'Carlsbad', lat: 33.1581, lng: -117.3506, note: 'Furniture and appliance hauls daily.' },
  { name: 'Encinitas', lat: 33.037, lng: -117.292, note: 'Yard waste and remodel debris.' },
  { name: 'Del Mar', lat: 32.9595, lng: -117.2653, note: 'Estate cleanouts and single pickups.' },
  { name: 'Vista', lat: 33.2, lng: -117.2425, note: 'Construction cleanup and dump runs.' },
  { name: 'San Marcos', lat: 33.1434, lng: -117.1661, note: 'Storage units and garage clears.' },
  { name: 'Escondido', lat: 33.1192, lng: -117.0864, note: 'Commercial and residential hauling.' },
  { name: 'Bonsall', lat: 33.2887, lng: -117.2258, note: 'Property and acreage cleanups.' },
]

export const serviceAreas: string[] = serviceAreaLocations.map((area) => area.name)

export interface Reason {
  icon: LucideIcon
  title: string
  body: string
}

export const reasons: Reason[] = [
  {
    icon: MapPinIcon,
    title: 'Local Expert',
    body: "We're not a faceless corporation. We're your neighbor. We know North County inside and out.",
  },
  {
    icon: CalendarClockIcon,
    title: 'Same-Day Service Available',
    body: 'Call today, we could be there tomorrow. No weeks of waiting.',
  },
  {
    icon: ReceiptTextIcon,
    title: 'Fair & Transparent Pricing',
    body: "We'll tell you what it costs before we start. No hidden charges, no surprises.",
  },
  {
    icon: ShieldCheckIcon,
    title: 'Professional & Reliable',
    body: 'We show up on time, we do the job right, and we respect your property.',
  },
  {
    icon: LayersIcon,
    title: 'Full-Service Solution',
    body: 'From a single couch to complete estate cleanouts — we handle jobs of all sizes.',
  },
]

export interface Step {
  title: string
  body: string
}

export const steps: Step[] = [
  {
    title: 'Call or Text Us',
    body: 'Tell us what needs to go — a photo over text works too. We listen.',
  },
  {
    title: 'Get Your Quote',
    body: 'Honest pricing right over the phone, before we roll out. No surprises.',
  },
  {
    title: 'We Haul It Away',
    body: 'Same-day or next-day. We load the trailer, sweep up, and your space is clean.',
  },
]

export interface CleanoutJob {
  id: string
  label: string
  location: string
  summary: string
  turnaround: string
  beforeImage: string
  afterImage: string
  /** Single photo that already shows the before and after side by side. */
  combinedImage?: string
}

export const cleanoutJobs: CleanoutJob[] = [
  {
    id: 'side-yard',
    label: 'Side Yard Cleanout',
    location: 'Vista, CA',
    summary:
      'A side yard packed wall-to-wall with trash bins, old clothing, cardboard and scrap — cleared down to a clean walkway you can actually use again.',
    turnaround: 'Same-day service',
    beforeImage: 'https://cdn.magicpatterns.com/uploads/2cVvrwFLEHRP6n8X8GUHTG/javi1.png',
    afterImage: 'https://cdn.magicpatterns.com/uploads/2cVvrwFLEHRP6n8X8GUHTG/javi1.png',
    combinedImage: 'https://cdn.magicpatterns.com/uploads/2cVvrwFLEHRP6n8X8GUHTG/javi1.png',
  },
  {
    id: 'lot-clearing',
    label: 'Lot & Brush Clearing',
    location: 'Escondido, CA',
    summary:
      'Overgrown trees, dead brush and fallen fruit taking over the back lot — cut down, loaded and hauled off, leaving clean graded dirt and an open view.',
    turnaround: 'Cleared in one day',
    beforeImage: 'https://cdn.magicpatterns.com/uploads/jS9473rA7qmiSbSZnK5LNE/javi2.png',
    afterImage: 'https://cdn.magicpatterns.com/uploads/aPoW4jDTg5m3Ky8iH7BA4E/javi_3.png',
  },
  {
    id: 'garage',
    label: 'Garage Cleanout',
    location: 'Carlsbad, CA',
    summary:
      'Broken furniture, two old appliances and years of boxes cleared out of a two-car garage.',
    turnaround: 'Done in one afternoon',
    beforeImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/cb1a1831-296d-49e7-8c37-5121cf833dab.jpg',
    afterImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/5246a97d-8f92-41cf-a65d-b967f613d4ee.jpg',
  },
  {
    id: 'yard',
    label: 'Yard Waste Cleanup',
    location: 'Oceanside, CA',
    summary:
      'Palm fronds, branches, broken fence boards and old planters hauled off the side yard.',
    turnaround: 'Next-day service',
    beforeImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/0e326795-122d-4763-9b57-97c387d3345c.jpg',
    afterImage:
      'https://cdn.magicpatterns.com/patterns/generated-images/e3f0c885-1606-4a5d-bd2a-eeed7ba8992b.jpg',
  },
]

export const dreamOutcomes: string[] = [
  'Your garage is actually usable again',
  'Your yard looks clean and organized',
  'No more walking past that pile of stress',
  'One simple phone call solved everything',
  'You knew exactly what it would cost — no surprises',
]
